import {
  AmountDimension,
  AmountLimitsDimension,
  BooleanDimension,
  CalendarDateDimension,
  ExclusionDimension,
  IntegerDimension,
  NumbersEnumDimension,
  StringsEnumDimension,
  StringsWithDataEnumDimension,
} from '@orus.eu/dimensions'
import {
  nlAovqlBenefitDurationEntries,
  nlAovqlIncapacityThresholds,
  nlAovqlProductVersions,
  nlAovqlProfessionalClasses,
  nlAovqlWaitingTimesInMonths,
} from '@orus.eu/product'

export const nlAovqlProductVersionDimension = new StringsEnumDimension({
  name: 'nlAovqlProductVersion',
  displayKeys: { name: 'dimension_name_nl_aovql_product_version' },
  values: nlAovqlProductVersions,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const nlAovqlExclusionsDimension = new ExclusionDimension({
  name: 'nlAovqlExclusions',
  displayKeys: { name: 'dimension_name_nl_aovql_exclusions' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: false },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const nlAovqlProfessionalClassDimension = new StringsEnumDimension({
  name: 'nlAovqlProfessionalClass',
  displayKeys: { name: 'dimension_name_nl_aovql_professional_class' },
  values: nlAovqlProfessionalClasses,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const nlAovqlContractDurationInYearsDimension = new IntegerDimension({
  name: 'nlAovqlContractDurationInYears',
  displayKeys: { name: 'dimension_name_nl_aovql_contract_duration_in_years' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const nlAovqlMaxBirthDateDimension = new CalendarDateDimension({
  name: 'nlAovqlMaxBirthDate',
  displayKeys: { name: 'dimension_name_nl_aovql_max_birth_date' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: false },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

type IncapacityThresholdState = { nlAovqlIncapacityThreshold?: string | number }

export const nlAovqlIncapacityThresholdInPercentDimension = new NumbersEnumDimension({
  name: 'nlAovqlIncapacityThresholdInPercent',
  displayKeys: { name: 'dimension_name_nl_aovql_incapacity_threshold' },
  initStrategy: {
    oldNames: ['nlAovqlIncapacityThreshold'],
    /**
     * The old name was only written by its `new` default, stored on every subscription whatever its product: 'P25'
     * from 2026-09-28 to 2026-10-15, then 25.
     */
    computeValue: ({ nlAovqlIncapacityThreshold }: IncapacityThresholdState) => {
      const percent =
        typeof nlAovqlIncapacityThreshold === 'string'
          ? Number(nlAovqlIncapacityThreshold.replace(/^P/, ''))
          : nlAovqlIncapacityThreshold
      return nlAovqlIncapacityThresholds.find((threshold) => threshold === percent)
    },
  },
  values: nlAovqlIncapacityThresholds,
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
  valuesAreAmount: false,
} as const)

export const nlAovqlMonthlyInsuredAmountDimension = new AmountDimension({
  name: 'nlAovqlMonthlyInsuredAmount',
  displayKeys: { name: 'dimension_name_nl_aovql_monthly_insured_amount' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const nlAovqlMonthlyInsuredAmountLimitsDimension = new AmountLimitsDimension({
  name: 'nlAovqlMonthlyInsuredAmountLimits',
  displayKeys: { name: 'dimension_name_nl_aovql_monthly_insured_amount_limits' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const nlAovqlIndexedMonthlyInsuredAmountDimension = new AmountDimension({
  name: 'nlAovqlIndexedMonthlyInsuredAmount',
  displayKeys: { name: 'dimension_name_nl_aovql_indexed_monthly_insured_amount' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const nlAovqlWaitingTimeInMonthsDimension = new NumbersEnumDimension({
  name: 'nlAovqlWaitingTimeInMonths',
  displayKeys: { name: 'dimension_name_nl_aovql_waiting_time_in_months' },
  values: nlAovqlWaitingTimesInMonths,
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
  valuesAreAmount: false,
} as const)

export const nlAovqlBenefitDurationDimension = new StringsWithDataEnumDimension({
  name: 'nlAovqlBenefitDuration',
  displayKeys: { name: 'dimension_name_nl_aovql_benefit_duration' },
  entries: nlAovqlBenefitDurationEntries,
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const nlAovqlFormsGateCompletedDimension = new BooleanDimension({
  name: 'nlAovqlFormsGateCompleted',
  displayKeys: { name: 'dimension_name_nl_aovql_forms_gate_completed' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
